import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { HeadingItem, Section } from '@atlaskit/menu';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue.graphql';
import type { issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion.graphql';
import { IssueWarningProvider } from '@atlassian/jira-software-releases-version-issue-warning-controller/src/ui/index.tsx';
import messages from './messages.tsx';

type Props = {
	jiraIssue: issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key;
	jiraVersion: issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key;
};
export const IssueWarning = ({ jiraIssue, jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const issue = useFragment(
		graphql`
			fragment issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue on JiraIssue {
				...ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraIssue
			}
		`,
		jiraIssue,
	);

	const version = useFragment(
		graphql`
			fragment issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion on JiraVersion {
				...ui_softwareReleasesVersionIssueWarningController_IssueWarningProvider_jiraVersion
			}
		`,
		jiraVersion,
	);

	return (
		<IssueWarningProvider jiraVersion={version} jiraIssue={issue}>
			{({
				hasOpenPullRequestWarning,
				hasFailingBuildsWarning,
				hasUnreviewedCodeWarning,
				hasOpenReviewWarning,
			}) => {
				const warningsMessages: MessageDescriptor[] = [];
				if (hasOpenPullRequestWarning) {
					warningsMessages.push(messages.openPullRequest);
				}
				if (hasFailingBuildsWarning) {
					warningsMessages.push(messages.failingBuilds);
				}
				if (hasUnreviewedCodeWarning) {
					warningsMessages.push(messages.unreviewedCode);
				}
				if (hasOpenReviewWarning) {
					warningsMessages.push(messages.openReviews);
				}
				if (warningsMessages.length === 1) {
					return (
						<Section>
							<HeadingItem>{formatMessage(messages.issueWarningHeader)}</HeadingItem>
							<WarningDescription>
								{formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.issueWarningIssueTerminologyRefreshM4
										: messages.issueWarning,
									{
										warning: formatMessage(warningsMessages[0]),
									},
								)}
							</WarningDescription>
						</Section>
					);
				}
				if (warningsMessages.length > 1) {
					return (
						<Section>
							<HeadingItem>{formatMessage(messages.issueWarningHeader)}</HeadingItem>
							<WarningDescription>
								{formatMessage(
									fg('jira-issue-terminology-update-m4')
										? messages.issueMultipleWarningsIssueTerminologyRefreshM4
										: messages.issueMultipleWarnings,
								)}
							</WarningDescription>
							<List>
								{warningsMessages.map((warning) => (
									<ListItem key={warning.id}>{formatMessage(warning)}</ListItem>
								))}
							</List>
						</Section>
					);
				}

				return null;
			}}
		</IssueWarningProvider>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ListItem = styled.li({
	color: `${token('color.text.accent.gray')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const List = styled.ul({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `0 0 ${gridSize * 2.5}px  0 `,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const WarningDescription = styled.p({
	margin: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	padding: `${gridSize * 1.25}px ${token('space.250')} ${token('space.250')} ${token('space.250')}`,

	color: `${token('color.text.accent.gray')}`,
});
